import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "../components/Logo";
import { ArrowLeft, FileText } from "lucide-react";

const ADMIN_EMAIL = "soporteclinped@expedienteelectronicoclinped.com";
const UPDATED = "10 de julio de 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-2">
      <h2 className="font-outfit font-bold text-lg text-brand-ink">{title}</h2>
      <div className="text-sm text-brand-ink/80 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background py-10 px-5" data-testid="terms-of-service">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl border border-stone-100 p-8 sm:p-10 space-y-7">
        <button onClick={() => navigate("/")} data-testid="terms-back-btn"
          className="flex items-center gap-1.5 text-muted-foreground hover:text-brand-ink text-sm font-semibold">
          <ArrowLeft size={16} /> Volver
        </button>

        <div className="flex items-center gap-3"><Logo size={38} /></div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-terracotta/10 flex items-center justify-center">
            <FileText className="text-brand-terracotta" size={26} />
          </div>
          <div>
            <h1 className="font-outfit font-extrabold text-2xl text-brand-ink">Términos y Condiciones</h1>
            <p className="text-sm text-muted-foreground">ClinPed · Última actualización: {UPDATED}</p>
          </div>
        </div>

        <Section title="1. Aceptación de los términos">
          <p>Al crear una cuenta y utilizar ClinPed (la "Aplicación"), usted acepta estos Términos y Condiciones.
          Si no está de acuerdo, no utilice la Aplicación.</p>
        </Section>

        <Section title="2. Descripción del servicio">
          <p>ClinPed es una herramienta de <b>expediente clínico electrónico</b> para uso profesional del médico,
          con funciones de historia clínica, recetas, estudios, consentimientos, agenda, sincronización opcional
          con Google Calendar y asistencia con inteligencia artificial. La Aplicación es una herramienta de apoyo
          y <b>no sustituye el juicio clínico</b> del profesional de la salud.</p>
        </Section>

        <Section title="3. Cuenta y uso profesional">
          <p>Usted es responsable de la veracidad de la información de su cuenta y de mantener la confidencialidad
          de sus credenciales. El uso de la Aplicación implica el cumplimiento de la normativa sanitaria aplicable
          (p. ej. NOM-004-SSA3-2012 y NOM-024-SSA3-2012 en México) y de la protección de datos de sus pacientes.</p>
        </Section>

        <Section title="4. Responsabilidad clínica">
          <p>Las sugerencias generadas por inteligencia artificial (dosis, diagnósticos, interpretaciones de
          estudios, recomendaciones) son un <b>apoyo</b>. La decisión, prescripción e interpretación final son
          responsabilidad exclusiva del médico tratante. ClinPed no se hace responsable del uso clínico que se dé
          a la información.</p>
        </Section>

        <Section title="5. Suscripción y pagos">
          <p>El acceso se ofrece mediante planes de suscripción (Básico, Odontológico y Pro). Los cobros se
          procesan a través de Stripe. Salvo indicación en contrario, los pagos son mensuales. La falta de pago
          suspende el acceso a las funciones de datos hasta la reactivación.</p>
        </Section>

        <Section title="6. Integración con Google Calendar">
          <p>La sincronización con Google Calendar es opcional. Al activarla, usted autoriza a ClinPed a gestionar
          eventos de calendario relacionados con sus citas. El uso de los datos de Google se rige por nuestro
          <b> Aviso de Privacidad</b> y por la Política de Datos de Usuario de los Servicios de la API de Google
          (incluido su requisito de Uso Limitado). Puede revocar el acceso en cualquier momento.</p>
        </Section>

        <Section title="7. Propiedad de los datos">
          <p>Los datos clínicos que usted captura son de su titularidad y responsabilidad como médico tratante.
          ClinPed actúa como encargado del tratamiento para prestar el servicio. Usted puede exportar o eliminar
          expedientes desde la Aplicación.</p>
        </Section>

        <Section title="8. Limitación de responsabilidad">
          <p>La Aplicación se ofrece "tal cual". En la medida permitida por la ley, ClinPed no será responsable de
          daños indirectos, pérdida de datos derivada de un mal uso, o interrupciones del servicio ajenas a su
          control razonable.</p>
        </Section>

        <Section title="9. Cambios y terminación">
          <p>Podemos actualizar estos Términos; la fecha de la última actualización se indica al inicio. Podemos
          suspender cuentas que incumplan estos Términos o la normativa aplicable.</p>
        </Section>

        <Section title="10. Contacto">
          <p>Para cualquier duda sobre estos Términos: <b>{ADMIN_EMAIL}</b></p>
        </Section>
      </motion.div>
    </div>
  );
}
